import {
    isPersistableIdentity,
    QUEUE_MAX_ENTRIES,
    type PlaybackSourceSlug,
    type QueueEntry,
    type QueueEntryRecordLinks,
    type QueueIdentity,
} from '@/types/playerQueue';
import type { PlaylistTrackRow } from '@/composables/api/usePlaylistSwitchingApi';

const STORAGE_KEY = 'player-queue';
const SNAPSHOT_VERSION = 1;

/**
 * What survives a reload: identities and position. Never audio, never a
 * presigned URL, never a blob — and not cover art either, which is itself a
 * signed URL that expires. A restored entry renders the cover placeholder until
 * it plays.
 */
export interface PersistedQueueSnapshot {
    version: number;
    entries: PersistedQueueEntry[];
    currentEntryId: string | null;
    repeatMode: string;
    shuffleEnabled: boolean;
    /**
     * The order the queue was in before it was shuffled, as entry ids.
     *
     * Persisted because `shuffleEnabled` was and this was not, which left
     * unshuffle with nothing to restore after a reload: the button went dark and
     * the queue did not move. Empty for a queue that was never shuffled, and
     * empty in any snapshot written before this field existed — which is why the
     * store degrades "shuffled with no recorded order" to shuffle-off rather
     * than trusting the flag.
     */
    preShuffleEntryIds: string[];
}

interface PersistedQueueEntry extends QueueEntryRecordLinks {
    entryId: string;
    identity: QueueIdentity;
    source: PlaybackSourceSlug;
    code: string;
    title: string | null;
    artist: string | null;
    album: string | null;
    /**
     * The track row the queue view renders its card from.
     *
     * Persisted, unlike the cover URL beside it, because the two fail
     * differently. A cover URL is signed and would come back expired — worse
     * than absent. A card is plain metadata that does not expire, and leaving it
     * out meant a reload silently downgraded every row in the queue to the bare
     * fallback with no way back: the densities stopped doing anything, and the
     * only cure was to re-play the list from its Tracks tab.
     *
     * Round-tripped as-is rather than validated field by field. It is display
     * data with no behaviour hanging off it, so the worst a malformed one can do
     * is render an odd-looking card — whereas a strict parse would throw the
     * whole snapshot away over a field nobody reads. `cover_art_signed_url` is
     * the one field stripped on write; see the writer.
     */
    card: PlaylistTrackRow | null;
}

const PLAYBACK_SOURCE_SLUGS: PlaybackSourceSlug[] = [
    'live_mp3',
    'live_hls',
    'backup_mp3',
    'ddex_mp3',
    'manual_mp3',
];

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function stringOrNull(value: unknown): string | null {
    return typeof value === 'string' ? value : null;
}

/**
 * A record id off a snapshot. Ids are numbers or strings depending on the
 * entity, and a snapshot written before record links existed simply has none —
 * which reads as null here, so the restored line renders as plain text.
 */
function recordIdOrNull(value: unknown): number | string | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }

    return typeof value === 'string' && value !== '' ? value : null;
}

/**
 * Rebuild an identity, or reject it.
 *
 * Only the two re-resolvable kinds round-trip. A `fixed_descriptor` carries a
 * URL that has expired by now and a `local_file` carries a File that died with
 * the tab, so both are dropped on write AND refused on read — a snapshot
 * written by an older build that contained them must not resurrect them.
 */
function parseIdentity(raw: unknown): QueueIdentity | null {
    if (!isRecord(raw)) {
        return null;
    }

    if (raw.kind === 'live_track' && typeof raw.code === 'string' && raw.code !== '') {
        return { kind: 'live_track', code: raw.code };
    }

    if (raw.kind === 'raw_ddex' && typeof raw.rawDdexTrackId === 'number') {
        return { kind: 'raw_ddex', rawDdexTrackId: raw.rawDdexTrackId };
    }

    return null;
}

function parseEntry(raw: unknown): PersistedQueueEntry | null {
    if (!isRecord(raw)) {
        return null;
    }

    if (typeof raw.entryId !== 'string' || raw.entryId === '') {
        return null;
    }

    const identity = parseIdentity(raw.identity);
    if (identity === null) {
        return null;
    }

    const source = PLAYBACK_SOURCE_SLUGS.includes(raw.source as PlaybackSourceSlug)
        ? (raw.source as PlaybackSourceSlug)
        : null;
    if (source === null) {
        return null;
    }

    return {
        entryId: raw.entryId,
        identity,
        source,
        code: typeof raw.code === 'string' ? raw.code : '',
        title: stringOrNull(raw.title),
        artist: stringOrNull(raw.artist),
        album: stringOrNull(raw.album),
        // Display data only, so it is taken as-is when it is an object and
        // dropped otherwise. A snapshot written before this field existed simply
        // has no `card`, and those entries come back as bare rows — which is
        // what they were.
        card: isRecord(raw.card) ? (raw.card as unknown as PlaylistTrackRow) : null,
        trackEntityId: stringOrNull(raw.trackEntityId),
        trackRecordId: recordIdOrNull(raw.trackRecordId),
        artistId: recordIdOrNull(raw.artistId),
        albumId: recordIdOrNull(raw.albumId),
    };
}

/**
 * Read the persisted queue, tolerating anything.
 *
 * A poison payload must not fail every reload forever, so an unparseable or
 * unrecognised snapshot CLEARS the key and returns null rather than throwing.
 * (A half-written persisted entry once blanked the whole app — see the
 * `isUsable` guard in `trackListExportStore`.)
 */
export function readPersistedQueue(maxEntries = QUEUE_MAX_ENTRIES): PersistedQueueSnapshot | null {
    if (typeof localStorage === 'undefined') {
        return null;
    }

    let raw: string | null = null;
    try {
        raw = localStorage.getItem(STORAGE_KEY);
    } catch {
        return null;
    }

    if (raw === null || raw === '') {
        return null;
    }

    let parsed: unknown = null;
    try {
        parsed = JSON.parse(raw);
    } catch {
        clearPersistedQueue();

        return null;
    }

    if (!isRecord(parsed) || parsed.version !== SNAPSHOT_VERSION || !Array.isArray(parsed.entries)) {
        clearPersistedQueue();

        return null;
    }

    const entries = parsed.entries
        .map(parseEntry)
        .filter((entry): entry is PersistedQueueEntry => entry !== null)
        .slice(0, maxEntries);

    if (entries.length === 0) {
        clearPersistedQueue();

        return null;
    }

    const currentEntryId = stringOrNull(parsed.currentEntryId);

    return {
        version: SNAPSHOT_VERSION,
        entries,
        // A current entry that didn't survive the filter falls back to the top
        // of the queue rather than leaving the position pointing at nothing.
        currentEntryId:
            currentEntryId !== null && entries.some((entry) => entry.entryId === currentEntryId)
                ? currentEntryId
                : entries[0].entryId,
        repeatMode: typeof parsed.repeatMode === 'string' ? parsed.repeatMode : 'off',
        shuffleEnabled: parsed.shuffleEnabled === true,
        // Tolerated, not required: a snapshot written before this field existed
        // reads back as an empty order, which the store treats as "not shuffled".
        // No SNAPSHOT_VERSION bump — an added optional field cannot be misread,
        // and bumping would throw away every live queue to add a nicety.
        preShuffleEntryIds: Array.isArray(parsed.preShuffleEntryIds)
            ? parsed.preShuffleEntryIds.filter((entryId): entryId is string => typeof entryId === 'string')
            : [],
    };
}

export function writePersistedQueue(options: {
    entries: QueueEntry[];
    currentEntryId: string | null;
    repeatMode: string;
    shuffleEnabled: boolean;
    preShuffleEntryIds?: string[];
}): void {
    if (typeof localStorage === 'undefined') {
        return;
    }

    // Manual uploads and fixed descriptors are dropped here, and the entries
    // around them are preserved — hence keying position by entryId rather than
    // by index, which would shift under the gap.
    const persistable = options.entries
        .filter((entry) => isPersistableIdentity(entry.identity))
        .map<PersistedQueueEntry>((entry) => ({
            entryId: entry.entryId,
            identity: entry.identity,
            source: entry.source,
            code: entry.code,
            title: entry.title,
            artist: entry.artist,
            album: entry.album,
            // The signed cover URL is stripped: it expires, and a card holding a
            // dead link renders a broken image where the fallback placeholder
            // would have been honest. Everything else on the card is plain
            // metadata that keeps.
            card: entry.card === null ? null : { ...entry.card, cover_art_signed_url: null },
            // Plain ids, unlike the signed cover URL above — safe to store, and
            // what keeps the restored bar's lines navigating.
            trackEntityId: entry.trackEntityId,
            trackRecordId: entry.trackRecordId,
            artistId: entry.artistId,
            albumId: entry.albumId,
        }));

    if (persistable.length === 0) {
        clearPersistedQueue();

        return;
    }

    const snapshot: PersistedQueueSnapshot = {
        version: SNAPSHOT_VERSION,
        entries: persistable,
        currentEntryId: options.currentEntryId,
        repeatMode: options.repeatMode,
        shuffleEnabled: options.shuffleEnabled,
        preShuffleEntryIds: options.preShuffleEntryIds ?? [],
    };

    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
    } catch {
        // Unavailable (private mode) or full. A queue is a convenience; failing
        // to save one must never break playback, so this stays silent.
    }
}

export function clearPersistedQueue(): void {
    if (typeof localStorage === 'undefined') {
        return;
    }

    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch {
        // Same as above — nothing actionable.
    }
}
